import React from "react";
import { useTranslation } from "react-i18next";
import { SvgIconComponent } from "@mui/icons-material";
import {
  AssessmentOutlined,
  BusinessCenterOutlined,
  BusinessOutlined,
  CampaignOutlined,
  DesignServicesOutlined,
  Diversity3Outlined,
  EventAvailableOutlined,
  GridViewOutlined,
  GroupsOutlined,
  HandshakeOutlined,
  HeadsetMicOutlined,
  HelpCenterOutlined,
  LibraryBooksOutlined,
  SupportAgentOutlined,
} from "@mui/icons-material";
import { Features, UserRole } from "../../Enums/companyStatus";
import { hasPermission, hasRole } from "../../Helpers/authHelpers";
import { useTerminology } from "../../Core/useTerminology";
import { useMyself } from "../../Query/useUsers";
import { DashboardModuleKey } from "../../Models/dashboard";
import { VOICES_FEATURES } from "../Voices/Navigation/voicesNavigation";
import {
  AMBASSADOR_FEATURES,
  AMBASSADOR_PATH,
} from "../Ambassador/Common/ambassadorNavigation";
import {
  BOOKING_APP_URL,
  MICROSOFT_365_URL,
  ZOHO_CRM_URL,
} from "../DashboardMenu/navigationItems";
import { DOCUMENT_CRAFTING_PATH } from "../DocumentCrafting/Common/documentCraftingNavigation";

export const ORDER_LINK = "https://corporatemomentum.zohobookings.eu/";

export interface DashboardModuleDefinition {
  key: DashboardModuleKey;
  title: string;
  description: string;
  icon: SvgIconComponent;
  path?: string;
  externalUrl?: string;
  hasAccess: boolean;
  /** Header style of the module card, fixed per module (see ModuleCard). */
  tone: ModuleTone;
}

export type ModuleTone = 0 | 1 | 2;

/**
 * Every module the home dashboard can show, in display order.
 *
 * Access mirrors the sidebar (`navigationItems.tsx`). Modules a user lacks are
 * kept only when they can be bought (shown locked); admin-only modules,
 * como's internal modules and como's external links are dropped for everyone
 * without access.
 */
export const useDashboardModules = (): DashboardModuleDefinition[] => {
  const { t } = useTranslation("home");
  const terminology = useTerminology();
  const { data: myself } = useMyself();
  const isAdmin = hasRole(UserRole.ADMIN);
  const can = (feature: Features) => isAdmin || hasPermission(feature);
  const showLocked = isAdmin || (myself?.show_locked_features ?? true);

  const text = (key: DashboardModuleKey) => ({
    title: t(`hub.modules.items.${key}.title`, {
      entity: terminology.plural,
    }),
    description: t(`hub.modules.items.${key}.description`, {
      entity: terminology.plural,
      entitySingular: terminology.singular,
    }),
  });

  const lockable: Omit<DashboardModuleDefinition, "tone">[] = [
    {
      key: "virtual_team_members",
      ...text("virtual_team_members"),
      icon: GroupsOutlined,
      path: "/como-experts",
      hasAccess: can(Features.COMO_EXPERTS),
    },
    {
      key: "voices",
      ...text("voices"),
      icon: CampaignOutlined,
      path: "/voices",
      hasAccess: isAdmin || VOICES_FEATURES.some(hasPermission),
    },
    {
      key: "ambassador",
      ...text("ambassador"),
      icon: Diversity3Outlined,
      path: AMBASSADOR_PATH,
      hasAccess: isAdmin || AMBASSADOR_FEATURES.some(hasPermission),
    },
    {
      key: "entities",
      ...text("entities"),
      icon: BusinessOutlined,
      path: "/entity",
      hasAccess: can(Features.ENTITY_CREATION),
    },
    {
      key: "evaluation",
      ...text("evaluation"),
      icon: AssessmentOutlined,
      path: "/evaluation",
      hasAccess: can(Features.EVALUATION),
    },
    {
      key: "question_lists",
      ...text("question_lists"),
      icon: HelpCenterOutlined,
      path: "/questions_list",
      hasAccess: can(Features.QUESTION_LISTS),
    },
    {
      key: "order_an_expert",
      ...text("order_an_expert"),
      icon: SupportAgentOutlined,
      path: "/expert-form",
      hasAccess: can(Features.ORDER_AN_EXPERT),
    },
  ];

  // como's own modules (como's corporate design); not for sale, so they are
  // never shown locked.
  const internal: Omit<DashboardModuleDefinition, "tone">[] = [
    {
      key: "document_crafting",
      ...text("document_crafting"),
      icon: DesignServicesOutlined,
      path: DOCUMENT_CRAFTING_PATH,
      hasAccess: can(Features.DOCUMENT_CRAFTING),
    },
  ];

  const always: Omit<DashboardModuleDefinition, "tone">[] = [
    {
      key: "help_desk",
      ...text("help_desk"),
      icon: HeadsetMicOutlined,
      path: "/help-desk",
      hasAccess: true,
    },
  ];

  // como's own tools; they are not for sale, so they are never shown locked.
  const externalLinks: Omit<DashboardModuleDefinition, "tone">[] = [
    {
      key: "booking_app",
      ...text("booking_app"),
      icon: EventAvailableOutlined,
      externalUrl: BOOKING_APP_URL,
      hasAccess: can(Features.COMO_EXTERNAL_LINKS),
    },
    {
      key: "zoho_crm",
      ...text("zoho_crm"),
      icon: HandshakeOutlined,
      externalUrl: ZOHO_CRM_URL,
      hasAccess: can(Features.COMO_EXTERNAL_LINKS),
    },
    {
      key: "microsoft_365",
      ...text("microsoft_365"),
      icon: BusinessCenterOutlined,
      externalUrl: MICROSOFT_365_URL,
      hasAccess: can(Features.COMO_EXTERNAL_LINKS),
    },
  ];

  const adminOnly: Omit<DashboardModuleDefinition, "tone">[] = isAdmin
    ? [
        {
          key: "knowledge_bases",
          ...text("knowledge_bases"),
          icon: LibraryBooksOutlined,
          path: "/knowledge_base/",
          hasAccess: true,
        },
        {
          key: "admin_dashboard",
          ...text("admin_dashboard"),
          icon: GridViewOutlined,
          path: "/admin-dashboard",
          hasAccess: true,
        },
      ]
    : [];

  // Tones follow the catalogue order, so a module keeps its look whatever
  // the user has or filters. Modules added later go last, so the existing
  // ones keep their tones.
  const catalogue = [
    ...lockable,
    ...always,
    ...externalLinks,
    ...adminOnly,
    ...internal,
  ].map((module, index) => ({
    ...module,
    // Shifts by one every three, so tones run diagonally in a 3-wide grid
    // instead of filling whole columns.
    tone: ((index + Math.floor(index / 3)) % 3) as ModuleTone,
  }));
  const toned = (key: DashboardModuleKey) =>
    catalogue.find((module) => module.key === key) as DashboardModuleDefinition;

  const available = [
    ...lockable.filter((module) => module.hasAccess),
    ...internal.filter((module) => module.hasAccess),
    ...always,
    ...externalLinks.filter((module) => module.hasAccess),
    ...adminOnly,
  ].map((module) => toned(module.key));
  const locked = showLocked
    ? lockable
        .filter((module) => !module.hasAccess)
        .map((module) => toned(module.key))
    : [];

  return [...available, ...locked];
};

export const openModule = (
  module: DashboardModuleDefinition,
  navigate: (path: string) => void,
) => {
  if (!module.hasAccess) {
    window.open(ORDER_LINK, "_blank", "noopener,noreferrer");
    return;
  }
  if (module.externalUrl) {
    window.open(module.externalUrl, "_blank", "noopener,noreferrer");
    return;
  }
  if (module.path) navigate(module.path);
};

export const ModuleIcon: React.FC<{
  module: Pick<DashboardModuleDefinition, "icon">;
  size?: number;
  color?: string;
}> = ({ module, size = 22, color }) => {
  const Icon = module.icon;
  return <Icon sx={{ fontSize: size, color }} />;
};
